/**
 * ProcessTable — таблица host-процессов с поиском, сортировкой,
 * кнопками SIGTERM / SIGKILL.
 *
 * Backend: GET /api/v1/devops/processes, POST /api/v1/devops/process/{pid}/kill
 */

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, RefreshCw, Skull, Loader2, ShieldAlert, Zap } from "lucide-react";
import {
  fetchNativeProcesses,
  killNativeProcess,
  type NativeProcess,
} from "../../lib/api";
import { cn } from "../../lib/cn";

type SortKey = "cpu" | "mem" | "pid" | "etime";

export function ProcessTable() {
  const [items, setItems] = useState<NativeProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("cpu");
  const [killingPid, setKillingPid] = useState<number | null>(null);
  const [confirmKill, setConfirmKill] = useState<{ pid: number; force: boolean; cmd: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  async function load() {
    try {
      setError(null);
      const r = await fetchNativeProcesses({ filter, sort, limit: 200 });
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filter, sort]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, filter, sort]);

  async function doKill(pid: number, force: boolean, confirm: boolean = false) {
    setKillingPid(pid);
    try {
      await killNativeProcess(pid, { force, confirm });
      setConfirmKill(null);
      // Подождём 300 мс и перезагрузим — у kernel обычно хватает времени убрать процесс из /proc
      setTimeout(load, 300);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // 409 — protected процесс, требует confirm
      if (msg.includes(" 409 ") || msg.includes("защищ")) {
        const proc = items.find((p) => p.pid === pid);
        setConfirmKill({ pid, force, cmd: proc?.command || "" });
      } else {
        setError(msg);
      }
    } finally {
      setKillingPid(null);
    }
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Поиск по cmd или пользователю…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small focus:outline-none focus:border-accent-500"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="px-2 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small"
          aria-label="Сортировка"
        >
          <option value="cpu">по CPU</option>
          <option value="mem">по RAM</option>
          <option value="pid">по PID</option>
          <option value="etime">по возрасту</option>
        </select>
        <button
          type="button"
          onClick={() => setAutoRefresh((v) => !v)}
          className={cn(
            "px-2 py-1.5 rounded-md border text-small inline-flex items-center gap-1.5",
            autoRefresh
              ? "border-accent-500/50 text-accent-500 bg-accent-500/10"
              : "border-border/60 text-fg-muted",
          )}
          title="Автообновление каждые 5 сек"
        >
          <RefreshCw className={cn("size-3.5", autoRefresh && "animate-spin-slow")} />
          {autoRefresh ? "live" : "пауза"}
        </button>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 text-fg-muted hover:bg-bg-subtle text-small"
          title="Обновить сейчас"
        >
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">{items.length} процессов</span>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-small text-danger">
          {error}
        </div>
      )}

      {/* Confirm-protected dialog */}
      {confirmKill && (
        <ConfirmKillDialog
          pid={confirmKill.pid}
          cmd={confirmKill.cmd}
          force={confirmKill.force}
          onConfirm={() => doKill(confirmKill.pid, confirmKill.force, true)}
          onCancel={() => setConfirmKill(null)}
        />
      )}

      {/* Table */}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead className="bg-bg-subtle/60">
              <tr className="text-left text-fg-subtle text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">PID</th>
                <th className="px-3 py-2">USER</th>
                <th className="px-3 py-2 text-right">CPU%</th>
                <th className="px-3 py-2 text-right">MEM%</th>
                <th className="px-3 py-2 text-right">RSS</th>
                <th className="px-3 py-2">UPTIME</th>
                <th className="px-3 py-2">CMD</th>
                <th className="px-3 py-2 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-fg-muted">
                    <Loader2 className="size-4 animate-spin inline-block mr-2" /> загрузка…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-fg-muted">
                    Нет процессов под фильтр
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <ProcessRow
                    key={p.pid}
                    p={p}
                    busy={killingPid === p.pid}
                    onKill={(force) => doKill(p.pid, force)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProcessRow({
  p,
  busy,
  onKill,
}: {
  p: NativeProcess;
  busy: boolean;
  onKill: (force: boolean) => void;
}) {
  const cpuHigh = p.cpu_percent >= 50;
  const memHigh = p.mem_percent >= 10;
  return (
    <tr className="border-t border-border/30 hover:bg-bg-subtle/30">
      <td className="px-3 py-1.5 text-fg-muted">{p.pid}</td>
      <td className="px-3 py-1.5 text-fg-muted truncate max-w-[110px]">{p.user}</td>
      <td className={cn("px-3 py-1.5 text-right tabular-nums", cpuHigh && "text-warning font-semibold")}>
        {p.cpu_percent.toFixed(1)}
      </td>
      <td className={cn("px-3 py-1.5 text-right tabular-nums", memHigh && "text-warning font-semibold")}>
        {p.mem_percent.toFixed(1)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-fg-muted">{formatBytes(p.rss_kb * 1024)}</td>
      <td className="px-3 py-1.5 text-fg-muted whitespace-nowrap">{formatDuration(p.uptime_seconds)}</td>
      <td className="px-3 py-1.5 max-w-[420px] truncate text-fg" title={p.command}>
        {p.command}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        <button
          type="button"
          onClick={() => onKill(false)}
          disabled={busy}
          className="px-2 py-1 rounded-md text-[11px] border border-warning/40 text-warning hover:bg-warning/10 disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-1"
          title="SIGTERM"
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : <Skull className="size-3" />}
          term
        </button>
        <button
          type="button"
          onClick={() => onKill(true)}
          disabled={busy}
          className="ml-1 px-2 py-1 rounded-md text-[11px] border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-50 disabled:cursor-wait inline-flex items-center gap-1"
          title="SIGKILL (force)"
        >
          <Zap className="size-3" />
          kill -9
        </button>
      </td>
    </tr>
  );
}

function ConfirmKillDialog({
  pid,
  cmd,
  force,
  onConfirm,
  onCancel,
}: {
  pid: number;
  cmd: string;
  force: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2"
    >
      <ShieldAlert className="size-4 mt-0.5 shrink-0 text-warning" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-small text-fg">
          Защищённый процесс — подтвердите {force ? "SIGKILL" : "SIGTERM"}
        </div>
        <div className="text-[11px] text-fg-muted mt-0.5">PID {pid}</div>
        <pre className="mt-1.5 px-2 py-1 rounded bg-bg-subtle text-[11px] font-mono text-fg-muted truncate">
          {cmd.length > 160 ? cmd.slice(0, 160) + "…" : cmd}
        </pre>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md text-small font-medium text-white"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            Да, убить
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-small border border-border/60 text-fg-muted hover:bg-bg-subtle"
          >
            Отмена
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────

function formatBytes(b: number): string {
  if (!b || b < 0) return "0";
  const u = ["B", "K", "M", "G", "T"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)}${u[i]}`;
}

function formatDuration(sec: number): string {
  if (!sec || sec < 0) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч`;
  if (h > 0) return `${h}ч ${m}м`;
  if (m > 0) return `${m}м`;
  return `${sec}с`;
}
